import React from "react";
import { Box, Button, TextField, MenuItem } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Header from "../../components/Header";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";

const subscriptionTypes = [
  { value: "", label: "None" },
  { value: "day", label: "Day" },
  { value: "monthly", label: "Monthly" },
  { value: "annual", label: "Annual" },
];

const subscriptionSessions = [
  { value: "", label: "None" },
  ...Array.from({ length: 13 }, (_, i) => ({
    value: i,
    label: i.toString(),
  })),
];

const ManageSubscription = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const subscription = location.state?.subscription || {};
  const navigate = useNavigate();

  const handleFormSubmit = async (values, {resetForm}) => {
    console.log(values);
    try {
      let response;
      if (values.subscriptionId) {
        // Update existing subscription
        response = await axios.put(
          `${config.apiBaseUrl}/update_subscription/${values.subscriptionId}`,
          values,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${document.cookie
                .split("; ")
                .find((row) => row.startsWith("teba_auth_token="))
                ?.split("=")[1]}`,
            },
          }
        );
        toast.success("Subscription updated successfully", {
          onClose: () => {
            navigate("/subscription_list");
          }
        });
      } else {
        // Add new subscription
        response = await axios.post(
          `${config.apiBaseUrl}/add_subscription`,
          values,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${document.cookie
                .split("; ")
                .find((row) => row.startsWith("teba_auth_token="))
                ?.split("=")[1]}`,
            },
          }
        );
        toast.success("Subscription added successfully");
        resetForm();
      }
      console.log(response.data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to save subscription");
    }
  };

  const initialValues = {
    subscriptionId : subscription.subscription_id || "",
    subscriptionName: subscription.subscription_name || "",
    subscriptionPrice: subscription.subscription_price || "",
    subscriptionType: subscription.subscription_type || "",
    subscriptionSession: subscription.subscription_session !== undefined ? subscription.subscription_session : "",
    subscriptionDesc: subscription.subscription_desc || "",
  };

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <Header title="ADD | MODIFY Subscription" subtitle="Create or Modify a Subscription" />
      <ToastContainer />
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap={{ xs: "14px", sm: "18px", md: "24px" }}
              gridTemplateColumns={{ xs: "repeat(1, minmax(0, 1fr))", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }}
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Subscription Name"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("subscriptionName", e.target.value)}
                value={values.subscriptionName}
                name="subscriptionName"
                error={!!touched.subscriptionName && !!errors.subscriptionName}
                helperText={touched.subscriptionName && errors.subscriptionName}
                sx={{ gridColumn: { xs: "span 1", sm: "span 2", lg: "span 2" } }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Subscription Price"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("subscriptionPrice", e.target.value)}
                value={values.subscriptionPrice}
                name="subscriptionPrice"
                error={!!touched.subscriptionPrice && !!errors.subscriptionPrice}
                helperText={touched.subscriptionPrice && errors.subscriptionPrice}
                sx={{ gridColumn: { xs: "span 1", sm: "span 1", lg: "span 1" } }}
              />
              <TextField
                fullWidth
                variant="filled"
                select
                label="Subscription Type"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("subscriptionType", e.target.value)}
                value={values.subscriptionType}
                name="subscriptionType"
                error={!!touched.subscriptionType && !!errors.subscriptionType}
                helperText={touched.subscriptionType && errors.subscriptionType}
                sx={{ gridColumn: { xs: "span 1", sm: "span 1", lg: "span 1" } }}
              >
                {subscriptionTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                variant="filled"
                select
                label="Subscription Session"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("subscriptionSession", e.target.value)}
                value={values.subscriptionSession}
                name="subscriptionSession"
                error={!!touched.subscriptionSession && !!errors.subscriptionSession}
                helperText={touched.subscriptionSession && errors.subscriptionSession}
                sx={{ gridColumn: { xs: "span 1", sm: "span 1", lg: "span 1" } }}
              >
                {subscriptionSessions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Subscription Description"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("subscriptionDesc", e.target.value)}
                value={values.subscriptionDesc}
                name="subscriptionDesc"
                error={!!touched.subscriptionDesc && !!errors.subscriptionDesc}
                helperText={touched.subscriptionDesc && errors.subscriptionDesc}
                sx={{ gridColumn: { xs: "span 1", sm: "span 2", lg: "span 2" } }}
                multiline
                rows={isMobile ? 3 : 2}
              />
            </Box>
            <Box display="flex" justifyContent="flex-start" flexWrap="wrap" gap={1} mt={{ xs: 3, sm: 4, md: 5 }}>
              <Button type="submit" color="secondary" variant="contained" size={isMobile ? "small" : "medium"}>
                Save
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const checkoutSchema = yup.object().shape({
  subscriptionName: yup.string().required("required"),
  subscriptionPrice: yup.number().required("required"),
  subscriptionType: yup.string().oneOf(["day", "monthly", "annual", ""]).nullable(),
  subscriptionSession: yup.number().min(0).max(12).nullable().transform((value, originalValue) => 
    originalValue === "" ? null : value
  ),
  subscriptionDesc: yup.string(),
});



export default ManageSubscription;